import {
  PersonalizedProductDetailsResponse,
  ProductPriceObj,
} from 'api/product/getPersonalizedProductDetails/types';
import { SavingPlanTypes } from 'api/savings-plans';
import cn from 'classnames';
import Price from 'components/price';
import { isProductEligibleForSavingsPlan } from 'components/saving-plan/utils';
import { OrderType } from 'constants/order';
import { useSavingsPlan } from 'hooks/savings-plan/use-savings-plan';
import { StaticPDPProduct, Variant } from 'modules/product-details/types';
import { useMemo } from 'react';
import { useSavingPlanStore } from 'stores/saving-plan';

export interface PriceElementProps {
  className?: string;
  couponPrices: ProductPriceObj | undefined;
  hasInfoPrice?: boolean;
  oneOffPrice?: number;
  orderType: OrderType;
  price?: number;
  pricePerKilo?: number;
  product?: StaticPDPProduct;
  selectedVariant?: Variant | undefined;
  selectedVariantDiscount: PersonalizedProductDetailsResponse['variants'][number];
  size?: 'small' | 'large' | 'xs';
  tags?: Array<string>;
  showComparePrice?: boolean;
}

const PriceElement: React.FC<PriceElementProps> = ({
  className,
  couponPrices,
  orderType,
  pricePerKilo,
  product,
  selectedVariantDiscount,
  size = 'large',
  tags,
  showComparePrice = true,
}) => {
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const { isPlanInCart } = useSavingsPlan();

  const priceDetails =
    orderType === OrderType.SUBSCRIPTION && selectedVariantDiscount.subscription
      ? selectedVariantDiscount.subscription
      : selectedVariantDiscount.one_off;

  const compareAtPrice: number = Number(priceDetails.compare?.before || priceDetails.price);

  const discountedPrice: number = Number(priceDetails.compare?.after || priceDetails.price);

  const isEligibleForSavingsPlan = useMemo(
    () => isProductEligibleForSavingsPlan(product?.productType),
    [product]
  );

  const shouldApplyDiscountColor = useMemo((): boolean => {
    const hasNaturalDiscount = compareAtPrice > discountedPrice;
    const hasActivePlanEligible = isEligibleForSavingsPlan && activePlan && !isPlanInCart;

    return hasNaturalDiscount || !!hasActivePlanEligible;
  }, [compareAtPrice, discountedPrice, isEligibleForSavingsPlan, activePlan, isPlanInCart]);

  if (!couponPrices) return null;

  const couponName = couponPrices.coupon?.code;
  const discountPercentage = couponPrices.coupon?.percentage;
  const hasCouponDiscount = discountPercentage && couponName && discountedPrice;

  const getDiscountColorClass = (): string => {
    if (activePlan && !isPlanInCart) {
      if (activePlan.planName === SavingPlanTypes.Clever) {
        return 'text-text-light-bg-clever-plan';
      }
      if (activePlan.planName === SavingPlanTypes.Premium) {
        return 'text-text-light-bg-premium-plan';
      }
    }
    return 'text-text-light-bg-discounted-price';
  };

  return (
    <>
      <div className={cn('h-14', !hasCouponDiscount && 'mt-2', className)}>
        <div className="flex flex-col items-end justify-end md:text-3xl">
          {hasCouponDiscount ? (
            <>
              <span className="text-text-secondary text-sm leading-[14px] line-through">
                <Price value={Number(compareAtPrice)} />
              </span>
              <span
                data-test="pdp-effective-price"
                className={cn(
                  getDiscountColorClass(),
                  'font-black',
                  size === 'xs' && 'text-base',
                  size === 'small' && 'text-lg',
                  size === 'large' && 'text-2xl'
                )}
              >
                <Price value={Number(discountedPrice)} />
              </span>
            </>
          ) : (
            <>
              {showComparePrice && compareAtPrice > discountedPrice && (
                <span className="text-text-secondary text-sm leading-[14px] line-through">
                  <Price value={Number(compareAtPrice)} />
                </span>
              )}
              <span
                data-test="pdp-effective-price"
                className={cn(
                  'leading-[30px] font-black',
                  size === 'xs' && 'text-base',
                  size === 'small' && 'text-lg',
                  size === 'large' && 'text-2xl',
                  shouldApplyDiscountColor && getDiscountColorClass()
                )}
              >
                <Price value={Number(discountedPrice)} />
              </span>
            </>
          )}
          {((product && !product.tags.includes('KGP')) || (tags && !tags.includes('KGP'))) &&
          pricePerKilo ? (
            <span className="text-text-secondary text-sm leading-3" data-test="price-per-kilo">
              <Price value={pricePerKilo} />
              /kg
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default PriceElement;
